import { useMemo, useState } from "react";
import { X } from "lucide-react";
import { cn } from "@/utils/helpers";
import type { CalendarPlanSlot } from "@/types/contentCalendar.type";
import { kitButtonClass } from "../../components/kit/Button";
import { KitIconButton } from "../../components/kit/IconButton";
import { MONTH_NAMES, slotUtcDay } from "../calendar.utils";
import { moveConsequence } from "./monthModel";
import {
  compactMonthDays,
  dayMonthShort,
  dayShort,
  dropRule,
  todayUtc,
  type CalendarPost,
} from "./calendarModel";
import { CalendarConfirm, CalendarSheet } from "./CalendarSheet";
import { CompactMonth } from "./PhoneCalendar";

type Move = { slotId: string; day: number; position: number };

export default function MovePostDialog({
  slot,
  slots,
  year,
  month,
  onMove,
  onClose,
  phone = false,
  posts = [],
  clientName = null,
  closed = false,
}: {
  slot: CalendarPlanSlot;
  slots: CalendarPlanSlot[];
  year: number;
  month: number;
  onMove: (move: Move) => Promise<void> | void;
  onClose: () => void;
  /** On a phone: Calendar / Move · Phone, a sheet with the month grid (d7G1r0). */
  phone?: boolean;
  /** The month's posts as the calendar reads them, for the grid's dots. */
  posts?: CalendarPost[];
  clientName?: string | null;
  closed?: boolean;
}) {
  const originalDay = slotUtcDay(slot.scheduledAt);
  const originalPosition = useMemo(
    () =>
      [...slots]
        .filter((item) => slotUtcDay(item.scheduledAt) === originalDay)
        .sort((a, b) => a.order - b.order)
        .findIndex((item) => item.id === slot.id),
    [originalDay, slot.id, slots],
  );
  const [day, setDay] = useState(originalDay);
  const [position, setPosition] = useState(Math.max(0, originalPosition));

  const targetSlots = useMemo(
    () =>
      [...slots]
        .filter((item) => item.id !== slot.id && slotUtcDay(item.scheduledAt) === day)
        .sort((a, b) => a.order - b.order),
    [day, slot.id, slots],
  );
  const normalizedPosition = Math.min(position, targetSlots.length);
  const unchanged = day === originalDay && normalizedPosition === originalPosition;
  const today = new Date();
  const consequence = moveConsequence(
    slot,
    Date.UTC(year, month - 1, day),
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()),
  );

  // Act on the confirm: the move is optimistic in the cache, and a refusal
  // rolls the month back with its own toast.
  const submit = () => {
    if (unchanged) return;
    void onMove({ slotId: slot.id, day, position: normalizedPosition });
    onClose();
  };

  const monthName = MONTH_NAMES[month - 1];
  const from = new Date(Date.UTC(year, month - 1, originalDay));
  const chosenKey = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const days = compactMonthDays(year, month, posts, { todayUtc: todayUtc(Date.now()), chosenKey });
  const grid = (
    <CompactMonth
      days={days}
      label={`${monthName} ${year}`}
      allowed={(d) => dropRule(slot, d.cell, { monthName, closed, clientName }).allowed}
      onChoose={(d) => {
        const postsOnDay = slots.filter(
          (item) => item.id !== slot.id && slotUtcDay(item.scheduledAt) === d.cell.day,
        ).length;
        setDay(d.cell.day);
        setPosition(d.cell.day === originalDay ? Math.max(0, originalPosition) : postsOnDay);
      }}
    />
  );
  const description = `${slot.title}, now on ${dayMonthShort({ weekday: from.getUTCDay(), day: originalDay, month })}.`;

  if (phone) {
    // Calendar / Move · Phone (d7G1r0): grabber, title and close, the month,
    // the compact month, then Cancel and Move sharing the foot. Moving to
    // another month waits on the server (the move endpoint keeps a post inside
    // its plan's month), so the month arrows are not drawn.
    return (
      <CalendarSheet phone label="Move to another day" onClose={onClose} flush>
        <div className="flex flex-col">
          <header className="flex items-start gap-2 pb-2 pl-4 pr-2 pt-1">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <h2 className="font-body text-[17px] font-semibold leading-normal text-co-text">Move to another day</h2>
              <p className="text-[13px] leading-[1.5] text-co-text2">{description}</p>
            </div>
            <KitIconButton kind="close" size="xl" icon={X} iconClassName="size-3.5" aria-label="Close" onClick={onClose} />
          </header>
          <p className="flex h-11 items-center pl-4 pr-2 text-[15px] font-semibold text-co-text">
            {monthName} {year}
          </p>
          <div className="px-4 py-2">{grid}</div>
          {consequence ? <p className="px-4 text-[13px] leading-[1.5] text-co-muted">{consequence}</p> : null}
          <div className="flex gap-2 p-4">
            <button type="button" onClick={onClose} className={cn(kitButtonClass({ variant: "ghost" }), "h-12 flex-1")}>
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={unchanged}
              className={cn(kitButtonClass({ variant: "primary" }), "h-12 flex-1")}
            >
              Move
            </button>
          </div>
        </div>
      </CalendarSheet>
    );
  }

  return (
    <CalendarConfirm
      phone={false}
      title="Move to another day"
      description={description}
      onClose={onClose}
      tone="primary"
      cancelLabel="Cancel"
      confirmLabel={unchanged ? "Already there" : `Move to ${dayShort({ weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(), day })}`}
      onConfirm={submit}
      confirmDisabled={unchanged}
    >
      <p className="text-[15px] font-semibold text-co-text">
        {monthName} {year}
      </p>
      {grid}
      {consequence ? <p className="text-[13px] leading-[1.5] text-co-muted">{consequence}</p> : null}
    </CalendarConfirm>
  );
}
